import { drawQr } from './qr';
import type { QrCardOptions, QrCardTemplate } from '@/api/reviews';

/** Карточка QR для печати (Алексей 22–23.10, #1976 → #2009): шаблоны. Surf-филиалам —
 *  три в стиле Surf (волна / бумага / чёрная, как форма отзыва), остальным — три своих.
 *  Главный заголовок — «Обратная связь управляющему». Всё рисуется на canvas в размере
 *  420 × 592 «точек» (A6) и масштабируется: превью, печать и PNG — одна картинка. */

export const CARD_ASPECT = 592 / 420;
export const DEFAULT_HEADLINE = 'Обратная связь управляющему';

/** Встроенные фоны; bg_id −N — N-й из списка, null — фон шаблона по умолчанию. */
// photo1–9 — фото владельца (Алексей 23.10, #2020); новые добавлять только в конец: bg_id −N сохранены в формах.
export const PRESET_BGS = ['travertine', 'plaster', 'flatlay', 'marble', 'moody', 'wave', 'counter',
  ...Array.from({ length: 9 }, (_, i) => `photo${i + 1}`)].map((k) => `/qr/bg-${k}.jpg`);
const presetId = (k: string) => -(PRESET_BGS.indexOf(`/qr/bg-${k}.jpg`) + 1);

export interface TemplateSpec {
  key: QrCardTemplate;
  label: string;
  /** null — шаблон без фото. */
  bg: number | null;
  tagline: string;
  surf: boolean;
  /** У шаблона есть полупрозрачная рамка вокруг кода. */
  frame: boolean;
}

export const TEMPLATES: TemplateSpec[] = [
  { key: 'surf_wave', label: 'Волна', bg: presetId('wave'), tagline: 'Покоряем волны вместе — подскажите, что сделать лучше', surf: true, frame: false },
  { key: 'surf_paper', label: 'Стрит', bg: presetId('counter'), tagline: 'SURFING NEVER ALONE', surf: true, frame: false },
  { key: 'surf_black', label: 'Чёрная', bg: null, tagline: 'Наведите камеру', surf: true, frame: false },
  { key: 'poster', label: 'Постер', bg: presetId('travertine'), tagline: 'Наведите камеру телефона', surf: false, frame: true },
  { key: 'journal', label: 'Журнал', bg: presetId('plaster'), tagline: 'Наведите камеру телефона', surf: false, frame: false },
  { key: 'glass', label: 'Стекло', bg: presetId('moody'), tagline: 'Наведите камеру — сообщение придёт управляющему лично', surf: false, frame: true },
];
export const templateOf = (key: string | undefined, surf: boolean) =>
  TEMPLATES.find((t) => t.key === key && t.surf === surf) ?? TEMPLATES.find((t) => t.surf === surf)!;

export const DEFAULT_CARD: QrCardOptions = {
  template: null, bg_id: null, title: '', legend: DEFAULT_HEADLINE, tagline: null, frame_opacity: 0.3,
};

/** Путь фона: null — без фото. */
export function bgSrcFor(card: QrCardOptions, tpl: TemplateSpec, uploaded: (id: number) => string | null): string | null {
  const id = card.bg_id ?? tpl.bg;
  if (id == null || id === 0) return null;
  if (id < 0) return PRESET_BGS[-id - 1] ?? null;
  return uploaded(id);
}

// ── шрифты: свои woff2 (кириллица + латиница), canvas без них рисует системным ──
const FONT_FILES: [string, string, string][] = [
  ['CardOswald', 'normal', 'oswald'], ['CardUnbounded', 'normal', 'unbounded'], ['CardPlayfair', 'normal', 'playfair'],
  ['CardPlayfair', 'italic', 'playfair-italic'], ['CardManrope', 'normal', 'manrope'],
];
const CYR = 'U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116';
const LAT = 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
let fontsReady: Promise<void> | null = null;
export function ensureCardFonts(): Promise<void> {
  if (!fontsReady) {
    const faces = FONT_FILES.flatMap(([family, style, file]) => [
      new FontFace(family, `url(/fonts/card/${file}-cyrillic.woff2)`, { style, weight: '100 900', unicodeRange: CYR }),
      new FontFace(family, `url(/fonts/card/${file}-latin.woff2)`, { style, weight: '100 900', unicodeRange: LAT }),
    ]);
    fontsReady = Promise.all(faces.map((f) => f.load().then((ff) => { document.fonts.add(ff); }).catch(() => undefined)))
      .then(() => undefined);
  }
  return fontsReady;
}

export interface CardInput {
  url: string;
  logoSrc: string | null;
  card: QrCardOptions;
  /** null — без фото. */
  bg: HTMLImageElement | null;
  /** Логотип Surf (штамп) — для Surf-шаблонов. */
  surfLogo: HTMLImageElement | null;
}

type Ctx = CanvasRenderingContext2D;

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number | [number, number, number, number]) {
  const [tl, tr, br, bl] = typeof r === 'number' ? [r, r, r, r] : r;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.arcTo(x + w, y, x + w, y + h, tr);
  ctx.arcTo(x + w, y + h, x, y + h, br);
  ctx.arcTo(x, y + h, x, y, bl);
  ctx.arcTo(x, y, x + w, y, tl);
  ctx.closePath();
}

function drawCover(ctx: Ctx, img: HTMLImageElement, x: number, y: number, w: number, h: number, posY = 0.5) {
  const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / s;
  const sh = h / s;
  ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) * posY, sw, sh, x, y, w, h);
}

/** Текст с разрядкой (canvas letterSpacing есть не везде — раскладываем сами). */
function tracked(ctx: Ctx, text: string, cx: number, y: number, track: number, align: 'center' | 'left' | 'right' = 'center') {
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) + track * Math.max(0, chars.length - 1);
  let x = align === 'center' ? cx - total / 2 : align === 'right' ? cx - total : cx;
  ctx.textAlign = 'left';
  chars.forEach((c, i) => { ctx.fillText(c, x, y); x += widths[i] + track; });
  return total;
}

function trackedWidth(ctx: Ctx, text: string, track: number) {
  const chars = [...text];
  return chars.reduce((a, c) => a + ctx.measureText(c).width, 0) + track * Math.max(0, chars.length - 1);
}

/** Строки под ширину с подбором кегля; track — разрядка в долях кегля. */
function fit(ctx: Ctx, text: string, font: (px: number) => string, start: number, floor: number, maxW: number, maxLines: number, track = 0) {
  const words = text.split(/\s+/).filter(Boolean);
  for (let px = start; px >= floor; px -= Math.max(1, px * 0.05)) {
    ctx.font = font(px);
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const next = cur ? `${cur} ${w}` : w;
      if (!cur || trackedWidth(ctx, next, px * track) <= maxW) cur = next;
      else { lines.push(cur); cur = w; }
    }
    if (cur) lines.push(cur);
    if (lines.length <= maxLines && lines.every((l) => trackedWidth(ctx, l, px * track) <= maxW)) return { px, lines };
  }
  ctx.font = font(floor);
  return { px: floor, lines: [text] };
}

function drawLines(ctx: Ctx, lines: string[], px: number, cx: number, top: number, lh: number, track = 0, align: 'center' | 'left' = 'center') {
  ctx.textBaseline = 'top';
  lines.forEach((l, i) => tracked(ctx, l, cx, top + i * px * lh, px * track, align));
  return top + lines.length * px * lh;
}

function whiteLogo(img: HTMLImageElement, w: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.round(w);
  c.height = Math.round((w * img.naturalHeight) / img.naturalWidth);
  const x = c.getContext('2d')!;
  x.drawImage(img, 0, 0, c.width, c.height);
  // Штамп — чёрный на белом: превращаем в белый на прозрачном (тёмное → белое непрозрачное).
  const d = x.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < d.data.length; i += 4) {
    const a = 255 - (d.data[i] + d.data[i + 1] + d.data[i + 2]) / 3;
    d.data[i] = d.data[i + 1] = d.data[i + 2] = 255;
    d.data[i + 3] = Math.round((a * d.data[i + 3]) / 255);
  }
  x.putImageData(d, 0, 0);
  return c;
}

function blackLogo(img: HTMLImageElement, w: number): HTMLCanvasElement {
  const c = whiteLogo(img, w);
  const x = c.getContext('2d')!;
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = '#111111';
  x.fillRect(0, 0, c.width, c.height);
  return c;
}

/** QR на белой скруглённой подложке; box — сторона подложки, pad — поле внутри. */
async function qrTile(ctx: Ctx, input: CardInput, x: number, y: number, box: number, pad: number, radius: number, opts: { shadow?: number; border?: number } = {}) {
  ctx.save();
  if (opts.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = opts.shadow; ctx.shadowOffsetY = opts.shadow / 3; }
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, x, y, box, box, radius);
  ctx.fill();
  ctx.restore();
  if (opts.border) {
    ctx.save();
    ctx.lineWidth = opts.border;
    ctx.strokeStyle = '#111111';
    roundRect(ctx, x + opts.border / 2, y + opts.border / 2, box - opts.border, box - opts.border, radius);
    ctx.stroke();
    ctx.restore();
  }
  const side = box - pad * 2;
  const qr = document.createElement('canvas');
  // quiet-зона у drawQr своя (4 модуля) — подложка и есть поле, поэтому рисуем чуть крупнее и обрезаем край.
  await drawQr(qr, input.url, { size: Math.round(side * 2), background: 'white', logoSrc: input.logoSrc });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const q = qr.width;
  const trim = q * 0.045;
  ctx.drawImage(qr, trim, trim, q - trim * 2, q - trim * 2, x + pad, y + pad, side, side);
}

const OSWALD = (px: number) => `600 ${px}px CardOswald, 'Arial Narrow', sans-serif`;
const UNBOUNDED = (px: number) => `800 ${px}px CardUnbounded, 'Arial Black', sans-serif`;
const PLAYFAIR = (w: number, italic = false) => (px: number) => `${italic ? 'italic ' : ''}${w} ${px}px CardPlayfair, Georgia, serif`;
const MANROPE = (w: number) => (px: number) => `${w} ${px}px CardManrope, system-ui, sans-serif`;

export async function renderQrCard(canvas: HTMLCanvasElement, input: CardInput, width: number, surf: boolean) {
  await ensureCardFonts();
  const W = Math.round(width);
  const H = Math.round(W * CARD_ASPECT);
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const s = W / 420;
  const { card, bg } = input;
  const tpl = templateOf(card.template ?? undefined, surf);
  const headline = (card.legend || '').trim();
  const branch = (card.title || '').trim();
  const tagline = (card.tagline ?? tpl.tagline).trim();
  // В Surf-шаблонах бренд уже есть (логотип, «SURF COFFEE®») — из названия филиала его убираем.
  const upperBranch = (tpl.surf ? branch.replace(/^surf\s*coffee\s*[x×·:-]?\s*/i, '') : branch).toUpperCase();

  switch (tpl.key) {
    case 'surf_wave': {
      if (bg) drawCover(ctx, bg, 0, 0, W, H, 0.6); else { ctx.fillStyle = '#3b3632'; ctx.fillRect(0, 0, W, H); }
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, 'rgba(0,0,0,0.35)'); g.addColorStop(0.45, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,0.5)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      let y = 28 * s;
      if (input.surfLogo) { const l = whiteLogo(input.surfLogo, 74 * s); ctx.drawImage(l, (W - l.width) / 2, y); y += l.height + 16 * s; }
      ctx.fillStyle = '#ffffff';
      if (headline) {
        const t = fit(ctx, headline.toUpperCase(), OSWALD, 42 * s, 24 * s, W - 52 * s, 3, 0.01);
        ctx.font = OSWALD(t.px); drawLines(ctx, t.lines, t.px, W / 2, y, 0.98, 0.01);
      }
      let b = H - 24 * s;
      if (tagline) {
        const t = fit(ctx, tagline, MANROPE(500), 12 * s, 9 * s, W - 52 * s, 2);
        ctx.font = MANROPE(500)(t.px); ctx.globalAlpha = 0.88;
        b -= t.lines.length * t.px * 1.3; drawLines(ctx, t.lines, t.px, W / 2, b, 1.3); ctx.globalAlpha = 1; b -= 6 * s;
      }
      const foot = ['SURF COFFEE®', upperBranch].filter(Boolean).join(' · ');
      ctx.font = MANROPE(600)(11 * s); b -= 11 * s;
      ctx.textBaseline = 'top'; tracked(ctx, foot, W / 2, b, 11 * s * 0.3);
      const box = 196 * s;
      await qrTile(ctx, input, (W - box) / 2, b - 16 * s - box, box, 12 * s, 24 * s, { shadow: 30 * s });
      break;
    }
    case 'surf_paper': {
      ctx.fillStyle = '#f1ede4'; ctx.fillRect(0, 0, W, H);
      const m = 14 * s;
      ctx.lineWidth = 2 * s; ctx.strokeStyle = '#111111';
      roundRect(ctx, m + s, m + s, W - 2 * m - 2 * s, H - 2 * m - 2 * s, 18 * s); ctx.stroke();
      const ix = m + 20 * s;
      const iw = W - 2 * ix;
      let y = m + 20 * s;
      if (input.surfLogo) { const l = blackLogo(input.surfLogo, 64 * s); ctx.drawImage(l, (W - l.width) / 2, y); y += l.height + 12 * s; }
      ctx.fillStyle = '#111111';
      if (headline) {
        const t = fit(ctx, headline.toUpperCase(), UNBOUNDED, 27 * s, 16 * s, iw, 3, -0.01);
        ctx.font = UNBOUNDED(t.px); y = drawLines(ctx, t.lines, t.px, W / 2, y, 1.02, -0.01) + 14 * s;
      }
      const footY = H - m - 16 * s - 10.5 * s;
      ctx.font = MANROPE(800)(10.5 * s); ctx.textBaseline = 'top';
      if (upperBranch) tracked(ctx, upperBranch, ix, footY, 10.5 * s * 0.22, 'left');
      if (tagline) {
        const maxW = iw - (upperBranch ? trackedWidth(ctx, upperBranch, 10.5 * s * 0.22) + 16 * s : 0);
        const t = fit(ctx, tagline.toUpperCase(), MANROPE(800), 10.5 * s, 7 * s, maxW, 1, 0.22);
        ctx.font = MANROPE(800)(t.px); tracked(ctx, t.lines[0], W - ix, footY, t.px * 0.22, 'right');
      }
      const ph = { x: ix, y, w: iw, h: footY - 12 * s - y };
      ctx.save(); roundRect(ctx, ph.x, ph.y, ph.w, ph.h, 12 * s); ctx.clip();
      if (bg) drawCover(ctx, bg, ph.x, ph.y, ph.w, ph.h); else { ctx.fillStyle = '#e4ded2'; ctx.fillRect(ph.x, ph.y, ph.w, ph.h); }
      ctx.restore();
      const box = Math.min(190 * s, ph.h * 0.86);
      await qrTile(ctx, input, W / 2 - box / 2, ph.y + ph.h / 2 - box / 2, box, 10 * s, 18 * s, { border: 2 * s });
      break;
    }
    case 'surf_black': {
      ctx.fillStyle = '#0d0d0d'; ctx.fillRect(0, 0, W, H);
      if (bg) { ctx.globalAlpha = 0.35; drawCover(ctx, bg, 0, 0, W, H); ctx.globalAlpha = 1; }
      let y = 28 * s;
      if (input.surfLogo) { const l = whiteLogo(input.surfLogo, 84 * s); ctx.drawImage(l, (W - l.width) / 2, y); y += l.height + 18 * s; }
      ctx.fillStyle = '#ffffff';
      if (headline) {
        const t = fit(ctx, headline.toUpperCase(), PLAYFAIR(500), 29 * s, 16 * s, W - 56 * s, 3, 0.08);
        ctx.font = PLAYFAIR(500)(t.px); y = drawLines(ctx, t.lines, t.px, W / 2, y, 1.15, 0.08);
      }
      ctx.globalAlpha = 0.5; ctx.fillRect(W / 2 - 24 * s, y + 18 * s, 48 * s, Math.max(1, s)); ctx.globalAlpha = 1;
      const box = 204 * s;
      await qrTile(ctx, input, (W - box) / 2, y + 34 * s, box, 12 * s, 22 * s);
      let b = H - 28 * s;
      if (upperBranch) {
        ctx.font = MANROPE(600)(10 * s); ctx.globalAlpha = 0.6; ctx.textBaseline = 'top';
        b -= 10 * s; tracked(ctx, upperBranch, W / 2, b, 10 * s * 0.3); ctx.globalAlpha = 1; b -= 12 * s;
      }
      if (tagline) {
        const t = fit(ctx, tagline.toUpperCase(), MANROPE(600), 11 * s, 8 * s, W - 100 * s, 1, 0.18);
        ctx.font = MANROPE(600)(t.px);
        const tw = trackedWidth(ctx, t.lines[0], t.px * 0.18);
        const ph = t.px + 18 * s;
        const pw = tw + 36 * s;
        b -= ph;
        ctx.strokeStyle = '#ffffff'; ctx.lineWidth = Math.max(1, s);
        roundRect(ctx, (W - pw) / 2, b, pw, ph, ph / 2); ctx.stroke();
        ctx.textBaseline = 'middle'; tracked(ctx, t.lines[0], W / 2, b + ph / 2, t.px * 0.18);
      }
      break;
    }
    case 'poster': {
      if (bg) drawCover(ctx, bg, 0, 0, W, H); else { ctx.fillStyle = '#5a4a3a'; ctx.fillRect(0, 0, W, H); }
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, 'rgba(28,20,12,0.72)'); g.addColorStop(0.38, 'rgba(28,20,12,0.25)');
      g.addColorStop(0.55, 'rgba(28,20,12,0)'); g.addColorStop(1, 'rgba(28,20,12,0.55)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#ffffff';
      let y = 34 * s;
      if (upperBranch) { ctx.font = MANROPE(600)(11 * s); ctx.textBaseline = 'top'; tracked(ctx, upperBranch, W / 2, y, 11 * s * 0.28); y += 25 * s; }
      if (headline) {
        const t = fit(ctx, headline, PLAYFAIR(600), 38 * s, 22 * s, W - 60 * s, 3);
        ctx.font = PLAYFAIR(600)(t.px); drawLines(ctx, t.lines, t.px, W / 2, y, 1.04);
      }
      let b = H - 26 * s;
      if (tagline) {
        const t = fit(ctx, tagline, MANROPE(600), 13 * s, 9 * s, W - 60 * s, 2);
        ctx.font = MANROPE(600)(t.px); b -= t.lines.length * t.px * 1.3; drawLines(ctx, t.lines, t.px, W / 2, b, 1.3); b -= 16 * s;
      }
      const fr = 226 * s;
      const fx = (W - fr) / 2;
      const fy = b - fr;
      ctx.save();
      ctx.fillStyle = `rgba(255,255,255,${card.frame_opacity})`;
      roundRect(ctx, fx, fy, fr, fr, 26 * s); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = Math.max(1, s); ctx.stroke();
      ctx.restore();
      await qrTile(ctx, input, fx + 13 * s, fy + 13 * s, fr - 26 * s, 10 * s, 18 * s);
      break;
    }
    case 'journal': {
      ctx.fillStyle = '#f4efe7'; ctx.fillRect(0, 0, W, H);
      const x0 = 30 * s;
      const iw = W - 60 * s;
      let y = 30 * s;
      if (upperBranch) {
        ctx.fillStyle = '#8a7a66'; ctx.font = MANROPE(800)(10 * s); ctx.textBaseline = 'top';
        tracked(ctx, upperBranch, x0, y, 10 * s * 0.24, 'left'); y += 26 * s;
      }
      if (headline) {
        // Последнее слово — курсивом и цветом, как в журнальной обложке.
        const words = headline.split(/\s+/);
        const last = words.pop() ?? '';
        const t = fit(ctx, headline, PLAYFAIR(700), 40 * s, 22 * s, iw, 3);
        ctx.textBaseline = 'top'; ctx.textAlign = 'left';
        t.lines.forEach((line, i) => {
          const ly = y + i * t.px * 1.0;
          if (i === t.lines.length - 1 && line.endsWith(last) && words.length) {
            const head = line.slice(0, line.length - last.length);
            ctx.font = PLAYFAIR(700)(t.px); ctx.fillStyle = '#2a2219'; ctx.fillText(head, x0, ly);
            const hw = ctx.measureText(head).width;
            ctx.font = PLAYFAIR(600, true)(t.px); ctx.fillStyle = '#9a6b3c'; ctx.fillText(last, x0 + hw, ly);
          } else {
            ctx.font = PLAYFAIR(700)(t.px); ctx.fillStyle = '#2a2219'; ctx.fillText(line, x0, ly);
          }
        });
        y += t.lines.length * t.px + 16 * s;
      }
      ctx.fillStyle = 'rgba(42,34,25,0.25)'; ctx.fillRect(x0, y, iw, Math.max(1, s)); y += 16 * s;
      const footY = H - 22 * s - 12 * s;
      const win = { x: x0, y, w: iw, h: footY - 14 * s - y };
      ctx.save(); roundRect(ctx, win.x, win.y, win.w, win.h, [win.w / 2, win.w / 2, 18 * s, 18 * s]); ctx.clip();
      if (bg) drawCover(ctx, bg, win.x, win.y, win.w, win.h, 0.7); else { ctx.fillStyle = '#e6dccd'; ctx.fillRect(win.x, win.y, win.w, win.h); }
      ctx.restore();
      const box = Math.min(188 * s, win.h * 0.72);
      await qrTile(ctx, input, W / 2 - box / 2, win.y + win.h - 18 * s - box, box, 10 * s, 18 * s, { shadow: 24 * s });
      if (tagline) {
        const t = fit(ctx, tagline, MANROPE(600), 12 * s, 8 * s, iw, 1);
        ctx.font = MANROPE(600)(t.px); ctx.fillStyle = '#2a2219'; ctx.textBaseline = 'top'; ctx.textAlign = 'left';
        ctx.fillText(t.lines[0], x0, footY);
      }
      break;
    }
    case 'glass': {
      if (bg) drawCover(ctx, bg, 0, 0, W, H); else { ctx.fillStyle = '#1a1714'; ctx.fillRect(0, 0, W, H); }
      const gw = 330 * s;
      const gx = (W - gw) / 2;
      // Высоту стекла считаем заранее: пилюля + заголовок + код + подпись.
      const t = headline ? fit(ctx, headline, MANROPE(800), 28 * s, 18 * s, gw - 48 * s, 3, -0.02) : null;
      const tg = tagline ? fit(ctx, tagline, MANROPE(500), 12.5 * s, 9 * s, gw - 48 * s, 2) : null;
      const box = 204 * s;
      const gh = 28 * s + (upperBranch ? 24 * s : 0) + (t ? t.lines.length * t.px * 1.05 + 32 * s : 0) + box + (tg ? 16 * s + tg.lines.length * tg.px * 1.35 : 0) + 22 * s;
      const gy = (H - gh) / 2;
      if (bg) {
        ctx.save(); roundRect(ctx, gx, gy, gw, gh, 30 * s); ctx.clip();
        ctx.filter = `blur(${Math.round(14 * s)}px)`; drawCover(ctx, bg, 0, 0, W, H); ctx.filter = 'none';
        ctx.restore();
      }
      ctx.save();
      ctx.fillStyle = `rgba(255,255,255,${Math.min(0.5, card.frame_opacity * 0.5)})`;
      roundRect(ctx, gx, gy, gw, gh, 30 * s); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = Math.max(1, s); ctx.stroke();
      ctx.restore();
      let y = gy + 28 * s;
      if (upperBranch) {
        ctx.font = MANROPE(800)(11 * s);
        const pw = trackedWidth(ctx, upperBranch, 11 * s * 0.06) + 24 * s;
        ctx.fillStyle = '#e8b77c'; roundRect(ctx, (W - pw) / 2, y - 2 * s, pw, 24 * s, 12 * s); ctx.fill();
        ctx.fillStyle = '#2a1a08'; ctx.textBaseline = 'middle'; tracked(ctx, upperBranch, W / 2, y + 10 * s, 11 * s * 0.06);
        y += 24 * s;
      }
      ctx.fillStyle = '#ffffff';
      if (t) { ctx.font = MANROPE(800)(t.px); y = drawLines(ctx, t.lines, t.px, W / 2, y + 14 * s, 1.05, -0.02) + 18 * s; }
      await qrTile(ctx, input, (W - box) / 2, y, box, 12 * s, 20 * s);
      y += box + 16 * s;
      if (tg) { ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = MANROPE(500)(tg.px); drawLines(ctx, tg.lines, tg.px, W / 2, y, 1.35); }
      break;
    }
  }
}

export function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function cardBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
  );
}
